import { ApplicationController } from '@aha-app/mvc';
import {
  type AssistantMessage,
  type Message,
  responseToMessage,
  veryLikelyAKeyword,
} from './AccountSearchAiChat.domain';
import { assistances } from 'javascripts/api';
import { debounce } from 'lodash';
import AhaDrawer from 'javascripts/drawer';
import AhaAnalytics from 'javascripts/analytics';

interface State {
  isLoading: boolean;
  isDebouncing: boolean;
  data: Message[];
  error?: string;
  abortController?: AbortController;
  isQueryActionable: boolean;
}

interface OpenDrawerOptions extends Record<string, unknown> {
  drawerable: boolean;
  url: string;
}

interface Props {
  initialQuery: string;
  cache: { messages: Message[] };
  closeModal: () => void;
}

const errorMessage: AssistantMessage = {
  role: 'assistant',
  text: '<filters></filters>',
  meta: {
    error: 'unknown error',
    content:
      'Oops. Something went wrong with that search. Please try again later.',
    type: 'unknown',
    records: [],
    raw: '',
  },
};

export default class FiltersAssistantController extends ApplicationController<
  State,
  Props
> {
  static initialState = {
    data: [],
    error: undefined,
    isLoading: false,
    isDebouncing: false,
    isQueryActionable: false,
  };

  cache: { messages: Message[] } = {
    messages: [],
  };
  abortController?: AbortController;

  async initialize({ initialQuery, cache }: Props) {
    this.cache = cache;
    this.state.isQueryActionable = !veryLikelyAKeyword(initialQuery);

    if (initialQuery) {
      await this.startConversation(initialQuery);
    }
  }

  async changeProps(newProps: Props, oldProps: Props) {
    const { initialQuery } = newProps;
    const queryChanged = initialQuery !== oldProps.initialQuery;
    this.state.isQueryActionable = !veryLikelyAKeyword(initialQuery);

    if (queryChanged) {
      if (initialQuery) {
        await this.startConversation(newProps.initialQuery);
      } else {
        this.clearState('query cleared');
      }
    }
  }

  async startConversation(initialQuery: string) {
    const { messages } = this.cache;
    const useCachedMessages = initialQuery === messages[0]?.text;

    if (useCachedMessages) {
      this.state.data = messages;
    } else {
      this.clearState('query changed');

      await this.actionDebouncedSendMessage(initialQuery);
    }
  }

  async actionDebouncedSendMessage(query: string) {
    this.state.isDebouncing = true;
    await this.debouncedSendMessage(query);
  }

  async actionSendMessage(query: string) {
    this.state.isDebouncing = false;
    const previousMessages = this.state.data.map(m => ({
      role: m.role,
      text: m.text,
    }));

    if (this.abortController) {
      this.abortController.abort('new request initiated');
    }

    const params = { prompt: query, previousMessages };

    if (previousMessages.length === 0 && !this.state.isQueryActionable) {
      // This is just saving a network request for when we are fairly certain is a keyword search.
      this.trackSendMessage(previousMessages.length, 'keywords');
      return;
    }

    this.addMessage({ role: 'user', text: query });

    try {
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.state.isLoading = true;
      const response = await assistances.filtersAssistant(params, signal);
      const message = responseToMessage(response);

      this.addMessage(message);
      this.state.error = undefined;
      this.trackSendMessage(previousMessages.length, message.meta.type);
    } catch (err) {
      if (err.name === 'AbortError') return;

      this.state.error = err;
      this.addMessage(errorMessage);
    } finally {
      this.cache.messages = this.state.data;
      this.state.isLoading = false;
      this.abortController = undefined;
    }
  }

  debouncedSendMessage = debounce(this.actionSendMessage, 1000);

  addMessage(message: Message) {
    // Messages must alternate roles and the first message must be from the user. Otherwise the assistant will errror.
    const lastMessage = this.state.data[this.state.data.length - 1];

    if (lastMessage && lastMessage.role === message.role) {
      this.state.data[this.state.data.length - 1] = message;
    } else if (lastMessage && lastMessage.role !== message.role) {
      this.state.data.push(message);
    } else if (!lastMessage && message.role === 'user') {
      this.state.data.push(message);
    }
  }

  clearState(reason?: string) {
    if (this.abortController) {
      this.abortController.abort(reason);
      this.abortController = undefined;
    }

    this.cache.messages = [];
    this.state.isLoading = false;
    this.state.data = [];
    this.state.error = undefined;
  }

  actionResultClick(options: OpenDrawerOptions) {
    this.trackEvent('Global search - User opens record from AI chat', {
      'record type': options.record_type,
    });

    this.props.closeModal();
    AhaDrawer.showUrl(options.url);
  }

  trackSendMessage(
    prevMessageCount: number,
    type: 'collection' | 'keywords' | 'unknown'
  ) {
    const collectionResponse = type === 'collection';
    if (prevMessageCount === 0) {
      const properties = { 'ai response': collectionResponse };
      this.trackEvent('Global search - User searches', properties);
    } else if (prevMessageCount === 2) {
      this.trackEvent('Global search - User asks followup question');
    }
  }

  actionTrackExpandChat() {
    const lastAssistantMessage = this.state.data
      .filter(m => m.role === 'assistant')
      .pop();
    if (!lastAssistantMessage) return;

    const recordTypes = lastAssistantMessage.meta.record_types;
    const filters = lastAssistantMessage.meta.filter_types;

    const properties = {
      'record type': recordTypes,
      'other filters': filters,
    };

    this.trackEvent('Global search - User expands chat', properties);
  }

  trackEvent(event: string, properties?: Record<string, any>) {
    AhaAnalytics.track('Feature-7412685970609879824', event, properties);
  }
}
